// SuggestionsExtension.ts
import { Extension } from '@tiptap/core'
import { Plugin, PluginKey, TextSelection, Transaction } from 'prosemirror-state'
import { Decoration, DecorationSet, EditorView } from 'prosemirror-view'
import { Editor } from "@tiptap/core";
import { realAIComplete } from './mockAIComplete'
import MarkdownIt from 'markdown-it';
const md = new MarkdownIt();

// Type definitions remain the same for compatibility
export type SuggestionType = 'sentenceCompletion' | 'listExpansion' | 'elaboration' | 'summary' | 'transition'

interface SuggestionsOptions {
  enabled: boolean
}

interface TypingSession {
  isActive: boolean
  startTime: number
  charCount: number
  lastTypingTime: number
}

interface SuggestionSession {
  lastAcceptedTime: number
  charactersTypedSinceAccept: number
  suggestionDisplayed: boolean
}

export interface SuggestionPluginState {
  decorations: DecorationSet
  activeSuggestion: string | null
  isLoading: boolean
  startPosition: number | null
  abortController: AbortController | null
  typingSession: TypingSession
  suggestionSession: SuggestionSession
  enabled: boolean
}

export interface ContentContext {
  text: string
  type: SuggestionType | null
  startPosition: number
}

// Configuration constants
const CONFIG = {
  TYPING_PAUSE_THRESHOLD: 2500,
  MIN_CHARS_FOR_SUGGESTION: 10,
  CHARS_AFTER_ACCEPT: 15,
  SUGGESTION_COOLDOWN: 5000,
  CONTEXT_LENGTH: 150,
  ACTIVE_TYPING_THRESHOLD: 5,
  ACTIVE_TYPING_WINDOW: 1000,
} as const

// Trigger patterns
const TRIGGER_PATTERNS = [
  {
    regex: /(?:[.!?]|^)\s*(?:\w+\s+){3,}$/,
    type: 'sentenceCompletion' as const,
    minLength: 30,
    cooldown: 5000,
  },
  {
    regex: /^[-*]\s+(?:\w+\s+){2,}$/m,
    type: 'listExpansion' as const,
    cooldown: 2000,
  },
  {
    regex: /\b(?:for example|specifically|such as|in particular)\s*$/,
    type: 'elaboration' as const,
    cooldown: 3000,
  },
  {
    regex: /\b(?:in conclusion|to summarize|overall|finally)\s*$/,
    type: 'summary' as const,
    cooldown: 4000,
  },
  {
    regex: /\b(?:furthermore|moreover|however|on the other hand)\s*$/,
    type: 'transition' as const,
    cooldown: 3000,
  },
]

export const suggestionPluginKey = new PluginKey<SuggestionPluginState>('suggestions')

class SuggestionManager {
  private view: EditorView
  private state: SuggestionPluginState
  private editor: Editor
  private suggestionTimer: NodeJS.Timeout | null = null
  private isDestroyed = false

  constructor(view: EditorView, state: SuggestionPluginState, editor: Editor) {
    this.view = view
    this.state = state
    this.editor = editor
  }

  // FIX 1: Improved state management with atomic updates
  private updateState(updates: Partial<SuggestionPluginState>, shouldDispatch = true): void {
    if (this.isDestroyed) return

    const newState = {
      ...this.state,
      ...updates,
    }

    this.state = newState

    if (shouldDispatch) {
      const tr = this.view.state.tr.setMeta(suggestionPluginKey, newState)
      this.view.dispatch(tr)
    }
  }

  // FIX 2: Comprehensive cleanup
  private cleanup(options: {
    clearTimer?: boolean,
    clearSuggestion?: boolean,
    abortRequest?: boolean
  } = {}): void {
    const {
      clearTimer = true,
      clearSuggestion = true,
      abortRequest = true
    } = options

    if (clearTimer && this.suggestionTimer) {
      clearTimeout(this.suggestionTimer)
      this.suggestionTimer = null
    }

    if (abortRequest && this.state.abortController) {
      this.state.abortController.abort()
      this.updateState({ abortController: null }, false)
    }

    if (clearSuggestion) {
      this.updateState({
        decorations: DecorationSet.empty,
        activeSuggestion: null,
        isLoading: false,
        startPosition: null,
      })
    }
  }

  // FIX 3: Improved session management
  private initializeNewTypingSession(): TypingSession {
    return {
      isActive: true,
      startTime: Date.now(),
      charCount: 0,
      lastTypingTime: Date.now(),
    }
  }

  // FIX 4: Better typing handling
  public handleDocChange(charCountDelta: number): void {
    if (this.isDestroyed) return

    const now = Date.now()

    if (charCountDelta > 0) {
      // Clear existing state immediately
      this.cleanup({ clearTimer: true, clearSuggestion: true, abortRequest: true })

      // Update sessions atomically
      const typingSession = this.updateTypingSession(charCountDelta, now)
      const suggestionSession = this.updateSuggestionSession(charCountDelta)

      this.updateState({
        typingSession,
        suggestionSession,
      })

      // Set up new suggestion timer if conditions are met
      if (typingSession.charCount >= CONFIG.MIN_CHARS_FOR_SUGGESTION) {
        this.debounceSuggestion()
      }
    }
  }

  private updateTypingSession(charCountDelta: number, now: number): TypingSession {
    return {
      isActive: true,
      startTime: this.state.typingSession.startTime,
      charCount: this.state.typingSession.charCount + charCountDelta,
      lastTypingTime: now,
    }
  }

  private updateSuggestionSession(charCountDelta: number): SuggestionSession {
    return {
      lastAcceptedTime: this.state.suggestionSession.lastAcceptedTime,
      charactersTypedSinceAccept:
        this.state.suggestionSession.charactersTypedSinceAccept + charCountDelta,
      suggestionDisplayed: false,
    }
  }

  // FIX 4: Improved debounce logic
  private debounceSuggestion(): void {
    this.cleanup({ clearTimer: true, clearSuggestion: false, abortRequest: false })

    this.suggestionTimer = setTimeout(() => {
      if (this.canShowSuggestions()) {
        const context = this.getContext(this.view.state.selection.from)
        if (context) {
          this.requestSuggestion(context)
        }
      }
    }, CONFIG.TYPING_PAUSE_THRESHOLD)
  }

  private canShowSuggestions(): boolean {
    if (
      this.isDestroyed ||
      this.state.isLoading ||
      this.state.suggestionSession.suggestionDisplayed ||
      !this.state.enabled
    ) {
      return false
    }

    const now = Date.now()
    const { typingSession, suggestionSession } = this.state

    return (
      now - suggestionSession.lastAcceptedTime > CONFIG.SUGGESTION_COOLDOWN &&
      now - typingSession.lastTypingTime >= CONFIG.TYPING_PAUSE_THRESHOLD &&
      suggestionSession.charactersTypedSinceAccept >= CONFIG.CHARS_AFTER_ACCEPT &&
      typingSession.charCount >= CONFIG.MIN_CHARS_FOR_SUGGESTION
    )
  }


  private getContext(pos: number): ContentContext | null {
    if (this.isDestroyed) return null

    const doc = this.view.state.doc
    const start = Math.max(0, pos - CONFIG.CONTEXT_LENGTH)
    const text = doc.textBetween(start, pos, ' ')

    // Check trigger patterns first
    for (const pattern of TRIGGER_PATTERNS) {
      if (pattern.regex.test(text)) {
        if (pattern.minLength && text.length < pattern.minLength) {
          continue
        }
        return { text, type: pattern.type, startPosition: pos }
      }
    }

    // Fall back to general suggestion if enough content
    return text.length >= CONFIG.MIN_CHARS_FOR_SUGGESTION
      ? { text, type: null, startPosition: pos }
      : null
  }

  // FIX 2: Improved request handling
  private async requestSuggestion(context: ContentContext): Promise<void> {
    if (this.isDestroyed) return

    try {
      // Clean up any existing request
      this.cleanup({ clearTimer: false, clearSuggestion: true, abortRequest: true })

      const abortController = new AbortController()
      this.updateState({
        abortController,
        decorations: this.createLoadingDecoration(context.startPosition),
        isLoading: true,
        startPosition: context.startPosition,
      })

      const suggestion = await realAIComplete(
        context.text.trim(),
        context.type,
        abortController.signal
      )

      if (!this.isDestroyed && !abortController.signal.aborted && suggestion) {
        this.updateState({
          decorations: this.createSuggestionDecoration(suggestion, context.startPosition),
          activeSuggestion: suggestion,
          isLoading: false,
          suggestionSession: {
            ...this.state.suggestionSession,
            suggestionDisplayed: true,
          },
        })
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Error creating suggestion:', error)
        this.cleanup({ clearTimer: true, clearSuggestion: true, abortRequest: true })
      }
    }
  }

  private createLoadingDecoration(pos: number): DecorationSet {
    return DecorationSet.create(this.view.state.doc, [
      Decoration.widget(pos, () => {
        const span = document.createElement('span')
        span.className = 'suggestion-loader'
        span.textContent = '●'
        return span
      }),
    ])
  }

  private createSuggestionDecoration(suggestion: string, pos: number): DecorationSet {
    return DecorationSet.create(this.view.state.doc, [
      Decoration.widget(pos, () => {
        const span = document.createElement('span')
        span.className = 'suggestion-widget'
        span.textContent = suggestion
        return span
      }),
    ])
  }

  public handleKeyDown(event: KeyboardEvent): boolean {
    if (this.isDestroyed) return false

    if (event.key === 'Escape') {
      this.cleanup({
        clearTimer: true,
        clearSuggestion: true,
        abortRequest: true,
      })
      event.preventDefault()
      event.stopPropagation()
      return true
    }

    if (this.state.isLoading && !this.isAllowedKey(event.key)) {
      event.preventDefault()
      return true
    }

    if (event.key === 'Tab' && this.state.activeSuggestion) {
      return this.applySuggestion()
    }

    return false
  }

  private isAllowedKey(key: string): boolean {
    return ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(key)
  }

  public applySuggestion(): boolean {
    if (this.isDestroyed || !this.state.activeSuggestion) return false;
  
    const pos = this.view.state.selection.from;
  
    // Render the suggestion as Markdown
    const markdownContent = md.renderInline(this.state.activeSuggestion);
  
    // Insert the content as parsed Markdown using commands.insertContent
    this.editor.commands.insertContentAt(pos, markdownContent);
  
    // Move the selection to the end of the inserted content
    const tr = this.view.state.tr;
    const newPos = pos + this.state.activeSuggestion.length;
  
    // Check if the new position is valid
    if (newPos <= tr.doc.content.size) {
        tr.setSelection(
            TextSelection.create(tr.doc, newPos)
        );
    } else {
        console.warn('New position is out of range:', newPos);
        // Optionally, you can set the selection to the end of the document
        tr.setSelection(
            TextSelection.create(tr.doc, tr.doc.content.size)
        );
    }
  
    // Update the state to clear the suggestion
    this.updateState({
        decorations: DecorationSet.empty,
        activeSuggestion: null,
        isLoading: false,
        startPosition: null,
        typingSession: this.initializeNewTypingSession(),
        suggestionSession: {
            lastAcceptedTime: Date.now(),
            charactersTypedSinceAccept: 0,
            suggestionDisplayed: false,
        },
    });
  
    this.view.dispatch(tr);
    return true;
  }

  public destroy(): void {
    this.isDestroyed = true
    this.cleanup()
  }
}

export const Suggestions = Extension.create<SuggestionsOptions>({
  name: 'suggestions',

  addOptions() {
    return {
      enabled: true,
    }
  },

  addProseMirrorPlugins() {
    const extensionOptions = this.options
    const editor = this.editor;

    return [
      new Plugin<SuggestionPluginState>({
        key: suggestionPluginKey,

        state: {
          init(): SuggestionPluginState {
            return {
              decorations: DecorationSet.empty,
              activeSuggestion: null,
              isLoading: false,
              startPosition: null,
              abortController: null,
              typingSession: {
                isActive: false,
                startTime: Date.now(),
                charCount: 0,
                lastTypingTime: Date.now(),
              },
              suggestionSession: {
                lastAcceptedTime: 0,
                charactersTypedSinceAccept: 0,
                suggestionDisplayed: false,
              },
              enabled: extensionOptions.enabled,
            }
          },

          apply(tr: Transaction, value: SuggestionPluginState): SuggestionPluginState {
            // Check for direct state update from meta
            const meta = tr.getMeta(suggestionPluginKey)
            if (meta) {
              return meta
            }

            // If disabled, keep clean state
            if (!value.enabled) {
              return {
                ...value,
                decorations: DecorationSet.empty,
                activeSuggestion: null,
                isLoading: false,
                startPosition: null,
              }
            }

            // Handle document changes only if enabled
            if (tr.docChanged) {
              return {
                ...value,
                decorations: value.decorations.map(tr.mapping, tr.doc),
              }
            }

            return value
          },
        },

        props: {
          decorations(state) {
            const pluginState = suggestionPluginKey.getState(state)
            return pluginState?.enabled ? pluginState.decorations : DecorationSet.empty
          },

          handleKeyDown(view, event) {
            const pluginState = suggestionPluginKey.getState(view.state)
            if (!pluginState?.enabled) return false

            const manager = new SuggestionManager(view, pluginState, editor)
            const result = manager.handleKeyDown(event)
            manager.destroy()
            return result
          },
        },

        view(editorView) {
          let manager: SuggestionManager | null = null

          return {
            update: (view, prevState) => {
              // Get plugin state
              const pluginState = suggestionPluginKey.getState(view.state)

              // Clean up manager if disabled
              if (!pluginState?.enabled) {
                if (manager) {
                  manager.destroy()
                  manager = null
                }
                return
              }

              // Create new manager if enabled and not exists
              if (!manager) {
                manager = new SuggestionManager(view, pluginState, editor)
              }

              // Handle document changes
              if (!prevState.doc.eq(view.state.doc)) {
                const delta = view.state.doc.textContent.length - prevState.doc.textContent.length
                if (delta > 0) {
                  manager.handleDocChange(delta)
                }
              }
            },

            destroy: () => {
              if (manager) {
                manager.destroy()
                manager = null
              }
            },
          }
        },
      }),
    ]
  },
})